import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useFormSelector } from '@eas/common-web';
import { CoverLetterFields } from '@modules/es-file/fields/cover-letter/cover-letter';
import { HistoryField } from '@composite/history/history-field';
import { Tabs } from '@composite/tabs/tabs';
import { TabsConfig } from '@composite/tabs/tabs-types';
import { Decision } from '@models';
import { useHistoryDescriptors } from './decision-history-descriptors';
import { AbstractFields } from './fields/abstract/abstract';
import { AttachmentFields } from './fields/attachment/attachment';
import { DecisionFields } from './fields/decision/decision';
import { DifferentOpinionFields } from './fields/different-opinion/different-opinion';
import {
  DecisionDocumentsFields,
  DecisionIndexesFields,
  DecisionJudgesAndProcessorsFields,
  DecisionParticipantsFields,
  DecisionRegulationsFields,
} from './fields/es-file/es-file-fields';
import { GeneralFields } from './fields/general/general';
import { JustificationFields } from './fields/justification/justification';
import { LegalSentenceFields } from './fields/legal-sentence/legal-sentence';

export function Fields() {
  const historyDescriptors = useHistoryDescriptors();
  const takeOverIndexes = useFormSelector(
    (data: Decision) => data.takeOverIndexes
  );

  const config: TabsConfig = [
    {
      label: (
        <FormattedMessage
          id="ES__DECISIONS__DETAIL__PANEL_TITLE__GENERAL"
          defaultMessage="Obecné"
        />
      ),
      key: 'GENERAL',
      content: <GeneralFields />,
    },
    {
      label: (
        <FormattedMessage
          id="ES__DECISIONS__DETAIL__PANEL_TITLE__PARTICIPANTS"
          defaultMessage="Účastníci řízení"
        />
      ),
      key: 'PARTICIPANTS',
      content: <DecisionParticipantsFields />,
    },
    {
      label: (
        <FormattedMessage
          id="ES__DECISIONS__DETAIL__PANEL_TITLE__INDEXES"
          defaultMessage="Indexy"
        />
      ),
      key: 'INDEXES',
      hide: takeOverIndexes === false,
      content: <DecisionIndexesFields />,
    },
    {
      label: (
        <FormattedMessage
          id="ES__DECISIONS__DETAIL__PANEL_TITLE__REGULATIONS"
          defaultMessage="Předpisy"
        />
      ),
      key: 'REGULATIONS',
      hide: takeOverIndexes === false,
      content: <DecisionRegulationsFields />,
    },
    {
      label: (
        <FormattedMessage
          id="ES__DECISIONS__DETAIL__PANEL_TITLE__DECISION"
          defaultMessage="Rozhodnutí"
        />
      ),
      key: 'DECISION',
      content: <DecisionFields />,
    },
    {
      label: (
        <FormattedMessage
          id="ES__DECISIONS__DETAIL__PANEL_TITLE__JUSTIFICATION"
          defaultMessage="Odůvodnění"
        />
      ),
      key: 'JUSTIFICATION',
      content: <JustificationFields />,
    },
    {
      label: (
        <FormattedMessage
          id="ES__DECISIONS__DETAIL__PANEL_TITLE__LEGAL_SENTENCE"
          defaultMessage="Právní věta"
        />
      ),
      key: 'LEGAL_SENTENCE',
      content: <LegalSentenceFields />,
    },
    {
      label: (
        <FormattedMessage
          id="ES__DECISIONS__DETAIL__PANEL_TITLE__ABSTRACT"
          defaultMessage="Abstrakt"
        />
      ),
      key: 'ABSTRACT',
      content: <AbstractFields />,
    },
    {
      label: (
        <FormattedMessage
          id="ES__DECISIONS__DETAIL__PANEL_TITLE__DIFFERENT_OPINION"
          defaultMessage="Odlišné stanovisko"
        />
      ),
      key: 'DIFFERENT_OPINION',
      content: <DifferentOpinionFields />,
    },
    {
      label: (
        <FormattedMessage
          id="ES__DECISIONS__DETAIL__PANEL_TITLE__ATTACHMENTS"
          defaultMessage="Přílohy"
        />
      ),
      key: 'ATTACHMENTS',
      content: <AttachmentFields />,
    },
    {
      label: (
        <FormattedMessage
          id="ES__DECISIONS__DETAIL__PANEL_TITLE__DOCUMENTS"
          defaultMessage="Dokumenty"
        />
      ),
      key: 'DOCUMENTS',
      content: <DecisionDocumentsFields />,
    },
    {
      label: (
        <FormattedMessage
          id="ES__DECISIONS__DETAIL__PANEL_TITLE__COVER_LETTER"
          defaultMessage="Přílohová obálka"
        />
      ),
      key: 'COVER_LETTER',
      content: <CoverLetterFields />,
    },
    {
      label: (
        <FormattedMessage
          id="ES__DECISIONS__DETAIL__PANEL_TITLE__JUDGES_AND_PROCESSORS"
          defaultMessage="Soudci a zpracovatelé"
        />
      ),
      key: 'JUDGES_AND_PROCESSORS',
      content: <DecisionJudgesAndProcessorsFields />,
    },
    {
      label: (
        <FormattedMessage
          id="ES__DECISION__DETAIL__PANEL_TITLE__HISTORY"
          defaultMessage="Historie"
        />
      ),
      key: 'HISTORY',
      content: <HistoryField descriptors={historyDescriptors} />,
    },
  ];

  return <Tabs config={config.filter((tab) => !tab.hide)} />;
}
